import { useRef, type ReactNode } from "react"

/** Matches `gap-3` (0.75rem) between slides for scroll position math */
export const COMING_NEXT_CAROUSEL_GAP_PX = 12

interface ComingNextCarouselProps {
  children: ReactNode
  slideCount: number
  activeIndex: number
  onActiveIndexChange: (index: number) => void
  carouselRef?: React.RefObject<HTMLDivElement | null>
}

export function ComingNextCarousel({
  children,
  slideCount,
  activeIndex,
  onActiveIndexChange,
  carouselRef: externalRef,
}: ComingNextCarouselProps) {
  const internalRef = useRef<HTMLDivElement | null>(null)
  const carouselRef = externalRef ?? internalRef

  function handleCarouselScroll() {
    if (!carouselRef.current) return
    const { scrollLeft, clientWidth } = carouselRef.current
    const slideWidth = clientWidth + COMING_NEXT_CAROUSEL_GAP_PX
    const next = Math.min(
      Math.max(0, Math.round(scrollLeft / slideWidth)),
      slideCount - 1,
    )
    if (next !== activeIndex) {
      onActiveIndexChange(next)
    }
  }

  return (
    <div
      ref={carouselRef}
      onScroll={handleCarouselScroll}
      className="mt-4 flex w-full min-w-0 snap-x snap-mandatory gap-3 overflow-x-auto pb-2 touch-pan-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {children}
    </div>
  )
}

export function scrollComingNextCarouselTo(
  carouselRef: React.RefObject<HTMLDivElement | null>,
  index: number,
) {
  if (!carouselRef.current) return
  const slideWidth = carouselRef.current.clientWidth + COMING_NEXT_CAROUSEL_GAP_PX
  carouselRef.current.scrollTo({ left: slideWidth * index, behavior: "smooth" })
}
